import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Clock, MapPin, Minus, Plus, Loader2, AlertCircle,
  ArrowLeft, Sparkles, Crown, ChevronDown,
} from 'lucide-react';
import { publicV2Api } from '../lib/api';

const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};
const formatTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};


// ─── Pass Selector card ────────────────────────────────────────────────────
const PassCard = ({ pass, qty, onChange, disabled }) => {
  const remaining = Math.max(0, pass.inventory_remaining || 0);
  const soldOut = remaining <= 0;
  const lowStock = !soldOut && remaining <= 5;

  return (
    <motion.div
      layout
      className={`pass-card ${pass.is_vip ? 'pass-card-vip' : ''} ${soldOut ? 'pass-card-soldout' : ''}`}
      data-testid={`pass-card-${pass._id}`}
    >
      <div className="pass-card-head">
        <div>
          <div className="flex items-center gap-2">
            {pass.is_vip && (
              <span className="pass-vip-pill" data-testid={`pass-vip-${pass._id}`}>
                <Crown size={11} /> VIP
              </span>
            )}
            <h4 className="pass-card-name">{pass.name}</h4>
          </div>
          {pass.description && (
            <p className="pass-card-desc">{pass.description}</p>
          )}
        </div>
        <div className="pass-card-price">
          <span className="pass-card-price-value">{fmtINR(pass.price)}</span>
          <span className="pass-card-price-unit">/ pass</span>
        </div>
      </div>

      <div className="pass-card-foot">
        {soldOut ? (
          <span className="pass-stock-text text-red-400">Sold out</span>
        ) : lowStock ? (
          <span className="pass-stock-text text-amber-400">Only {remaining} left</span>
        ) : (
          <span className="pass-stock-text text-zinc-500">{remaining} available</span>
        )}

        <div className="pass-stepper" data-testid={`pass-stepper-${pass._id}`}>
          <button
            type="button"
            className="pass-stepper-btn"
            onClick={() => onChange(Math.max(0, qty - 1))}
            disabled={qty <= 0 || disabled || soldOut}
            aria-label="Decrease"
            data-testid={`pass-decrement-${pass._id}`}
          >
            <Minus size={14} />
          </button>
          <span className="pass-stepper-qty" data-testid={`pass-qty-${pass._id}`}>{qty}</span>
          <button
            type="button"
            className="pass-stepper-btn"
            onClick={() => onChange(Math.min(remaining, qty + 1))}
            disabled={qty >= remaining || disabled || soldOut}
            aria-label="Increase"
            data-testid={`pass-increment-${pass._id}`}
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {pass.requires_manual_approval && (
        <div className="pass-vip-note">
          <Sparkles size={11} className="text-[var(--accent-gold)]" />
          <span>VIP pass — admin approval required after payment</span>
        </div>
      )}
    </motion.div>
  );
};


// ─── Page ──────────────────────────────────────────────────────────────────
export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantities, setQuantities] = useState({}); // {pass_type_id: qty}
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await publicV2Api.getEvent(slug);
        if (!alive) return;
        setEvent(res.data);
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) setError('not_found');
        else setError('error');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [slug]);

  const onQtyChange = (passId, qty) =>
    setQuantities((q) => ({ ...q, [passId]: qty }));

  const { totalAmount, totalPasses, items } = useMemo(() => {
    if (!event) return { totalAmount: 0, totalPasses: 0, items: [] };
    const items = [];
    let total = 0;
    let count = 0;
    for (const pt of event.pass_types || []) {
      const q = quantities[pt._id] || 0;
      if (q > 0) {
        items.push({ pass_type_id: pt._id, quantity: q, name: pt.name, price: pt.price });
        total += (pt.price || 0) * q;
        count += q;
      }
    }
    return { totalAmount: total, totalPasses: count, items };
  }, [event, quantities]);

  const proceedToCheckout = () => {
    if (totalPasses <= 0) return;
    // Stash selected pass-ids+quantities in sessionStorage so the checkout page
    // can survive a login redirect without re-selecting passes.
    sessionStorage.setItem('v2_pending_booking', JSON.stringify({
      event_id: event._id,
      event_slug: event.slug,
      event_title: event.title,
      event_date: event.event_date,
      event_time: event.event_time,
      venue: event.venue,
      items: items.map((i) => ({ pass_type_id: i.pass_type_id, name: i.name, price: i.price, quantity: i.quantity })),
      total: totalAmount,
      created_at: Date.now(),
    }));
    navigate(`/events/${slug}/checkout`);
  };

  // ── Loading & error states ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="events-loading" data-testid="event-loading">
        <Loader2 size={26} className="animate-spin text-[var(--accent-gold)]" />
        <p>Loading event…</p>
      </div>
    );
  }
  if (error === 'not_found') {
    return (
      <div className="events-error" data-testid="event-not-found">
        <AlertCircle size={28} className="text-red-400 mb-2" />
        <h2 className="text-white text-lg font-semibold mb-1">Event not found</h2>
        <p className="text-zinc-400 text-sm mb-4">This event may have been removed or unpublished.</p>
        <Link className="btn-gold" to="/events">Browse all events</Link>
      </div>
    );
  }
  if (error) {
    return (
      <div className="events-error" data-testid="event-error">
        <AlertCircle size={28} className="text-red-400 mb-2" />
        <p className="text-zinc-300">Couldn't load this event.</p>
        <button className="btn-gold mt-3" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const hasArtist = !!(event.artist_name || event.artist_description);
  const fallback = 'https://images.unsplash.com/photo-1571266028243-e4733b0f0bb0?w=1600&q=85';
  const heroImg = event.hero_image || event.mobile_image || fallback;
  const descLong = event.long_description || '';
  const descShort = event.short_description || '';
  const showFull = descExpanded || descLong.length <= 280;
  const visibleDesc = showFull ? descLong : (descLong ? descLong.slice(0, 280) + '…' : '');

  return (
    <div className="event-detail-page" data-testid="event-detail-page">
      <Helmet>
        <title>{`${event.title} · Scratch Unleashed`}</title>
        <meta name="description" content={descShort || `Book passes for ${event.title} at Scratch Unleashed.`} />
        <meta property="og:title" content={`${event.title} · Scratch Unleashed`} />
        <meta property="og:description" content={descShort || `Book passes for ${event.title}.`} />
        <meta property="og:image" content={heroImg} />
        <meta property="og:type" content="event" />
        {/* Schema.org Event */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: event.title,
          startDate: `${event.event_date}T${event.event_time || '21:00'}:00`,
          location: { '@type': 'Place', name: event.venue || 'Scratch Unleashed' },
          description: descShort,
          image: heroImg,
          offers: (event.pass_types || []).map((pt) => ({
            '@type': 'Offer',
            name: pt.name,
            price: pt.price,
            priceCurrency: 'INR',
            availability: pt.inventory_remaining > 0 ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
          })),
        })}</script>
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="event-hero" data-testid="event-hero">
        <img
          src={heroImg}
          alt={event.title}
          className="event-hero-img"
          loading="eager"
          onError={(e) => { e.currentTarget.src = fallback; }}
        />
        <div className="event-hero-overlay" />
        <Link to="/events" className="event-back-btn" data-testid="event-back-events">
          <ArrowLeft size={16} /> Events
        </Link>
        <div className="event-hero-content">
          <span className="event-hero-eyebrow">
            <Sparkles size={11} className="text-[var(--accent-gold)]" />
            Scratch Unleashed
          </span>
          <h1 className="event-hero-title" data-testid="event-title">{event.title}</h1>
          <div className="event-hero-meta">
            <span data-testid="event-date"><Calendar size={13} className="text-[var(--accent-gold)]" /> {formatDate(event.event_date)}</span>
            {event.event_time && <span data-testid="event-time"><Clock size={13} className="text-[var(--accent-gold)]" /> {formatTime(event.event_time)}</span>}
            {event.venue && <span data-testid="event-venue"><MapPin size={13} className="text-[var(--accent-gold)]" /> {event.venue}</span>}
          </div>
        </div>
      </section>

      {/* ── BODY ─────────────────────────────────────────────────────── */}
      <main className="event-body">
        {descShort && (
          <p className="event-shortdesc" data-testid="event-shortdesc">{descShort}</p>
        )}

        {descLong && (
          <div className="event-longdesc" data-testid="event-longdesc">
            <p>{visibleDesc}</p>
            {descLong.length > 280 && (
              <button
                type="button"
                className="event-readmore"
                onClick={() => setDescExpanded((x) => !x)}
                data-testid="event-readmore"
              >
                {descExpanded ? 'Show less' : 'Read more'}
                <ChevronDown size={14} className={`transition-transform ${descExpanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        )}

        {hasArtist && (
          <section className="event-artist" data-testid="event-artist">
            <p className="event-section-eyebrow">Featuring</p>
            <h3 className="event-artist-name">{event.artist_name}</h3>
            {event.artist_description && (
              <p className="event-artist-desc">{event.artist_description}</p>
            )}
          </section>
        )}

        {/* ── PASSES ─────────────────────────────────────── */}
        <section className="event-passes" data-testid="event-passes">
          <header className="event-section-header">
            <h2>Select Passes</h2>
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
              {(event.pass_types || []).length} option{event.pass_types?.length === 1 ? '' : 's'}
            </span>
          </header>
          <div className="event-passes-list">
            {(event.pass_types || []).length === 0 && (
              <div className="event-empty-passes" data-testid="event-empty-passes">
                <p className="text-zinc-400 text-sm">No passes available for this event yet.</p>
              </div>
            )}
            {(event.pass_types || []).map((pt) => (
              <PassCard
                key={pt._id}
                pass={pt}
                qty={quantities[pt._id] || 0}
                onChange={(q) => onQtyChange(pt._id, q)}
              />
            ))}
          </div>
        </section>
      </main>

      {/* ── STICKY CTA ───────────────────────────────────────────────── */}
      <AnimatePresence>
        {totalPasses > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="event-cta-bar"
            data-testid="event-cta-bar"
          >
            <div className="event-cta-bar-inner">
              <div>
                <p className="event-cta-count" data-testid="event-cta-count">
                  {totalPasses} pass{totalPasses === 1 ? '' : 'es'} selected
                </p>
                <p className="event-cta-total" data-testid="event-cta-total">{fmtINR(totalAmount)}</p>
              </div>
              <button
                type="button"
                className="event-cta-btn"
                onClick={proceedToCheckout}
                data-testid="event-proceed-checkout"
              >
                Proceed to Booking
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
